import * as React from 'react';
import AppBar from '@material-ui/core/AppBar';
import AppsIcon from '@material-ui/icons/Apps';
import Button from '@material-ui/core/Button';
import ContractorSearch from './ContractorSearch';
import IconButton from '@material-ui/core/IconButton';
import Logo from './Logo';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import PersonIcon from '@material-ui/icons/Person';
import Toolbar from '@material-ui/core/Toolbar';
import { CompanySearch } from './CompanySearch';
import { DeepReadonly } from 'utility-types';
import { Feature } from 'flagged';
import { FeatureFlagWrapper } from '@pec/aion-ui-core/features/featureFlags/components/FeatureFlagWrapper';
import { IOrganization, OrganizationFeature } from '@pec/aion-ui-core/interfaces/organization';
import { IUserOrganization } from '@pec/aion-ui-core/interfaces/userOrganization';
import { LanguageSwitcherComponent } from '@pec/aion-ui-i18next/components/LanguageSwitcher';
import { localizationFeatureFlag } from '@pec/aion-ui-i18next/constants';
import { Popover } from '@pec/aion-ui-core/components/Popover';
import { Theme, WithStyles, withStyles } from '@material-ui/core/styles';
import { useUserMenuLink } from '../../hooks/useUserMenuLink';
import { useTranslation } from 'react-i18next';

export const styles = (theme: Theme) => ({
  appBar: {
    zIndex: theme.zIndex.drawer + 1
  },
  button: {
    margin: theme.spacing(1)
  },
  switcherButton: {
    marginRight: theme.spacing(1)
  },
  switcherMenuHeader: {
    fontSize: 11,
    fontWeight: 600,
    color: theme.palette.text.secondary,
    lineHeight: 1.2,
    '&.Mui-disabled': {
      opacity: 1
    }
  },
  grow: {
    flexGrow: 1,
    '& a': {
      color: 'white'
    }
  },
  toolbar: {
    paddingLeft: 10
  }
});

export type OwnProps = {
  handleLogOut: () => void;
  userId?: string;
  userEmail: string | null;
  includeCompanySearch: boolean;
  organizationId?: string;
  userOrganizations: DeepReadonly<IUserOrganization[]> | null;
  organization: DeepReadonly<IOrganization> | null;
  hasFetchedUserOrganizations: boolean;
};

export type Props = WithStyles<typeof styles> & OwnProps;

export type State = {
  anchorEl?: HTMLElement;
};

const DesktopAppBarComponent: React.FC<Props> = ({
  classes,
  handleLogOut,
  userId,
  includeCompanySearch,
  organizationId,
  userOrganizations,
  organization,
  hasFetchedUserOrganizations,
  userEmail
}) => {
  const { t } = useTranslation();
  const vf1MyProfileLink = '/?hint=vf1profile';
  const {
    complyWorksApplicationSwitcherLink,
    instructorPortalLink,
    verisourceApplicationSwitcherLink,
    shouldShowApplicationSwitcher,
    shouldShowInstructorPortal
  } = useUserMenuLink(userOrganizations, organizationId);
  const myAccountLink = '/identity/Manage/Index';
  const [applicationSwitcherAnchorEl, setApplicationSwitcherAnchorEl] = React.useState<HTMLElement | null>(null);
  const isApplicationSwitcherOpen = Boolean(applicationSwitcherAnchorEl);

  const handleApplicationSwitcherClick = (event: React.MouseEvent<HTMLElement>) => {
    setApplicationSwitcherAnchorEl(event.currentTarget);
  };

  const handleApplicationSwitcherClose = () => {
    setApplicationSwitcherAnchorEl(null);
  };

  const showQuickSearch =
    organization?.features.includes(OrganizationFeature.Client) &&
    [OrganizationFeature.ComplianceUK, OrganizationFeature.ComplianceAUS].some(value =>
      organization?.features.includes(value)
    );

  const featureIsEnabledForClient: boolean = !!organization?.features.includes(OrganizationFeature.ContractorSearchBar);

  return (
    <AppBar position="fixed" className={classes.appBar}>
      <Toolbar className={classes.toolbar}>
        <div className={classes.grow}>
          {hasFetchedUserOrganizations && (
            <Logo organizationId={organizationId} userOrganizations={userOrganizations} organization={organization} />
          )}
        </div>
        {(showQuickSearch || featureIsEnabledForClient) && <ContractorSearch organization={organization} />}
        {includeCompanySearch && <CompanySearch />}
        <FeatureFlagWrapper>
          <Feature
            name={localizationFeatureFlag}
            render={(isEnabled: boolean) => isEnabled && <LanguageSwitcherComponent userId={userId} />}
          />
        </FeatureFlagWrapper>
        {shouldShowApplicationSwitcher && (
          <React.Fragment>
            <IconButton
              aria-label={t('components.desktopAppBar.applicationSwitcher', 'Application Switcher')}
              aria-controls="application-switcher-menu"
              aria-haspopup="true"
              color="inherit"
              className={classes.switcherButton}
              onClick={handleApplicationSwitcherClick}
            >
              <AppsIcon />
            </IconButton>
            <Menu
              id="application-switcher-menu"
              anchorEl={applicationSwitcherAnchorEl}
              open={isApplicationSwitcherOpen}
              onClose={handleApplicationSwitcherClose}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right'
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right'
              }}
            >
              <MenuItem disabled className={classes.switcherMenuHeader}>
                {t('components.desktopAppBar.veriforceSolutions', 'VERIFORCE SOLUTIONS')}
              </MenuItem>
              <MenuItem
                component="a"
                href={complyWorksApplicationSwitcherLink}
                onClick={handleApplicationSwitcherClose}
              >
                {t('components.desktopAppBar.complyWorks', 'ComplyWorks')}
              </MenuItem>
              <MenuItem component="a" href={verisourceApplicationSwitcherLink} onClick={handleApplicationSwitcherClose}>
                {t('components.desktopAppBar.verisource', 'Verisource')}
              </MenuItem>
              {shouldShowInstructorPortal && (
                <MenuItem component="a" href={instructorPortalLink} onClick={handleApplicationSwitcherClose}>
                  {t('components.desktopAppBar.instructorPortal', 'Instructor Portal')}
                </MenuItem>
              )}
            </Menu>
          </React.Fragment>
        )}
        <Popover
          id="menu-appbar"
          render={({ buttonProps, menuProps }) => (
            <div>
              <Button {...buttonProps} color="inherit" className={classes.button}>
                <PersonIcon /> {userEmail}
              </Button>
              <Menu
                {...menuProps}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right'
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right'
                }}
              >
                <FeatureFlagWrapper>
                  <Feature
                    name={'VF1MyProfile'}
                    render={(isEnabled: boolean) => (
                      <MenuItem component="a" href={isEnabled ? vf1MyProfileLink : myAccountLink}>
                        {t('components.desktopAppBar.veriforceProfile', 'VeriforceOne Profile')}
                      </MenuItem>
                    )}
                  />
                </FeatureFlagWrapper>
                <MenuItem component="a" href="/user">
                  {t('components.desktopAppBar.myProfile', 'My Profile')}
                </MenuItem>
                <MenuItem component="a" href="/eLearning">
                  {t('components.desktopAppBar.eLearning', 'eLearning')}
                </MenuItem>
                <MenuItem component="a" onClick={handleLogOut}>
                  {t('components.desktopAppBar.logOut', 'Log Out')}
                </MenuItem>
              </Menu>
            </div>
          )}
        />
      </Toolbar>
    </AppBar>
  );
};

export const DesktopAppBar = withStyles(styles)(DesktopAppBarComponent);
